import { type HTMLProps } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';

type sizes = 'small' | 'default' | 'medium' | 'large' | 'extra-large';

interface TextProps
  extends Omit<HTMLProps<HTMLParagraphElement>, 'ref' | 'as' | 'size'> {
  className?: string;
  css?: string;
  size?: sizes;
}

export const SmallTextStyles = css`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: ${({ theme }) => theme.lineHeight.SMALL};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const DefaultTextStyles = css`
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
  line-height: ${({ theme }) => theme.lineHeight.DEFAULT};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const MediumTextStyles = css`
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  line-height: ${({ theme }) => theme.lineHeight.MEDIUM};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const LargeTextStyles = css`
  font-size: ${({ theme }) => theme.fontSize.LARGE};
  line-height: ${({ theme }) => theme.lineHeight.LARGE};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
  margin-bottom: 25px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ExtraLargeTextStyles = css`
  font-size: ${({ theme }) => theme.fontSize.EXTRA_LARGE};
  line-height: ${({ theme }) => theme.lineHeight.EXTRA_LARGE};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.LIGHT};
  margin-bottom: 25px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    font-size: ${({ theme }) => theme.fontSize.LARGE};
    line-height: ${({ theme }) => theme.lineHeight.LARGE};
  }
`;

interface TextStyleProps {
  $size?: sizes;
  css?: string;
}

const TextWrapper = styled.p<TextStyleProps>`
  margin: 0;
  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return SmallTextStyles;
      case 'default':
        return DefaultTextStyles;
      case 'medium':
        return MediumTextStyles;
      case 'large':
        return LargeTextStyles;
      case 'extra-large':
        return ExtraLargeTextStyles;
      default:
        return DefaultTextStyles;
    }
  }}
`;

export const Text: React.ComponentType<TextProps> = React.forwardRef<
  HTMLParagraphElement,
  TextProps
>(function Text({ children, css, className, size, ...htmlProps }, ref) {
  return (
    <TextWrapper
      ref={ref}
      $size={size}
      className={className}
      css={css}
      {...htmlProps}>
      {children}
    </TextWrapper>
  );
});
